import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Trash2, FlaskConical, FolderPlus, Sparkles } from "lucide-react";
import { useAIResources } from "../../hooks/useAIResources";
import {
  listScanHistory, deleteScanHistory,
  listScanCollections, saveScanFavorite,
} from "../../lib/api";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};

export const NotebookHistory = () => {
  const navigate = useNavigate();
  const { patchValidation } = useAIResources();
  const [items, setItems] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(() => {
    Promise.all([listScanHistory(), listScanCollections()])
      .then(([hist, cols]) => { setItems(hist); setCollections(cols); })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    setItems((p) => p.filter((h) => h.id !== id));
    try { await deleteScanHistory(id); } catch { load(); }
  };

  const validateAgain = (idea) => {
    patchValidation({ form: { name: idea.name || "", does: idea.description || "", customer: idea.audience || "", problem: idea.problem || "", different: "" }, report: null });
    toast.success("Sent to Validation Lab");
    navigate("/ai-resources/validation-lab");
  };

  const saveToCollection = async (idea, collectionId, photo) => {
    try {
      await saveScanFavorite({ ...idea, photo_ref: idea.photo_ref || photo || "" }, collectionId);
      toast.success(collectionId ? "Saved to folder" : "Saved to Collections");
    } catch { toast.error("Could not save"); }
  };

  if (loaded && items.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-dashed border-gray-200 py-14 px-8 text-center max-w-2xl" data-testid="notebook-history-empty">
        <p className="text-[#687280]">No scans yet. Every scan you run in the AI Opportunity Scanner (or the mobile app) is saved here automatically.</p>
        <button onClick={() => navigate("/ai-resources/opportunity-scanner")} className="mt-5 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-6 py-2.5 text-sm font-semibold hover:bg-[#c5a030] transition-colors" data-testid="history-scan-cta">
          <Sparkles size={15} /> Open Scanner
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-3" data-testid="notebook-history">
      {items.map((h) => {
        const first = (h.ideas || [])[0] || {};
        const count = (h.ideas || []).length;
        return (
          <div key={h.id} className="bg-white rounded-2xl border border-gray-100 p-4 flex items-start gap-4" data-testid={`history-item-${h.id}`}>
            {h.photo_ref ? (
              <img src={h.photo_ref} alt="Scan" className="w-16 h-16 rounded-xl object-cover border border-gray-100 shrink-0" />
            ) : (
              <span className="w-16 h-16 rounded-xl bg-[#0F0F0F] text-[#D4AF37] flex items-center justify-center shrink-0"><Clock size={22} /></span>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-display font-bold text-[#0F0F0F] truncate">{first.name || h.label || "Scan"}</p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-xs text-[#9aa1ab] flex items-center gap-1"><Clock size={12} /> {fmtDate(h.created_at)}</span>
                {first.ai_score ? <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#0F0F0F] text-[#D4AF37]">Score {first.ai_score}</span> : null}
                {count > 1 && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-[#687280]">{count} ideas</span>}
              </div>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <button onClick={() => validateAgain(first)} className="inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3 py-1.5 border border-gray-200 text-[#374151] hover:border-[#D4AF37]/60 transition-colors" data-testid={`history-validate-${h.id}`}>
                  <FlaskConical size={13} /> Validate Again
                </button>
                <div className="inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-2.5 py-1 border border-gray-200 text-[#374151]">
                  <FolderPlus size={13} className="text-[#B8901F]" />
                  <select
                    onChange={(e) => { if (e.target.value !== "__") { saveToCollection(first, e.target.value === "__unsorted" ? "" : e.target.value, h.photo_ref); e.target.value = "__"; } }}
                    defaultValue="__"
                    className="bg-transparent outline-none cursor-pointer"
                    data-testid={`history-save-${h.id}`}
                  >
                    <option value="__">Save to…</option>
                    <option value="__unsorted">📥 Unsorted</option>
                    {collections.map((c) => <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <button onClick={() => remove(h.id)} className="text-[#687280] hover:text-red-500 transition-colors shrink-0" data-testid={`history-delete-${h.id}`} title="Delete">
              <Trash2 size={16} />
            </button>
          </div>
        );
      })}
    </div>
  );
};
